<template>
  <main>
    <div class="title is-4 has-text-weight-light">
      {{ table.name }}
      <favourite-toggler v-if="isAuth" :id="table.id" :isShort="false"></favourite-toggler>
      <div class="title is-6 has-text-grey m-t-sm">{{ table.measure }}</div>
    </div>

    <!-- <div class="columns is-vcentered">
      <div class="column">
        <b-field grouped>
          <b-field label="Опции">
            <b-switch v-model="domLayout" true-value="autoHeight" false-value="normal" size="is-small">
              Автовысота
            </b-switch>
          </b-field>
        </b-field>
      </div>
    </div> -->

    <div class="columns is-vcentered is-multiline">
      <div class="column">
        <b-field grouped group-multiline>
          <b-field v-for="(column, index) of dataColumns" :key="index">
            <b-checkbox @input="toggleColumnVisibility($event, column)" :value="!column.hide" size="is-small">
              {{ column.headerName }}
            </b-checkbox>
          </b-field>
        </b-field>

        <b-field grouped group-multiline>
          <b-field v-for="(level, index) of levels" :key="index">
            <b-checkbox @input="toggleLevel($event, level.level)" :value="isLevelSelected(level.level)" size="is-small">
              {{ level.name }}
            </b-checkbox>
          </b-field>

          <b-field>
            <b-switch size="is-small" aria-controls="seriesList" v-model="toggleSeries">Расширенная настройка</b-switch>
          </b-field>
        </b-field>
      </div>

      <div class="column is-narrow">
        <div class="buttons">
          <b-button @click="clearFilters" size="is-small" type="is-warning">Сбросить фильтры</b-button>

          <b-button @click="exportCSV" icon-left="file-delimited-outline" size="is-small">
            Экспорт CSV
          </b-button>
        </div>
      </div>
    </div>

    <b-collapse aria-id="seriesList" :open.sync="toggleSeries">
      <div class="columns">
        <div class="column">
          <div class="notification">
            <div class="columns is-multiline">
              <div class="column is-full" v-for="(row, index) of topLevelRows" :key="index">
                <TableSeriesLevel
                  :item="row"
                  :rows="tableData.rows"
                  :selectedSeries="selectedSeries"
                  @selectSeries="selectSeries"
                ></TableSeriesLevel>
              </div>
            </div>
          </div>
        </div>
      </div>
    </b-collapse>

    <div class="columns">
      <div class="column">
        <div :class="domLayout === 'normal' ? 'fixed-height' : 'full-height'">
          <ag-grid-vue
            id="grid"
            style="width: 100%; height: 100%;"
            class="ag-theme-balham"
            :columnDefs="columnDefs"
            :rowData="rowData"
            @gridReady="onGridReady"
            :modules="modules"
            :gridOptions="gridOptions"
            :domLayout="domLayout"
            :localeText="localeText"
            :rowSelection="rowSelection"
            :floatingFilter="true"
            :suppressRowClickSelection="true"
            :suppressColumnMoveAnimation="true"
            :frameworkComponents="frameworkComponents"
          ></ag-grid-vue>
        </div>
      </div>
    </div>

    <div class="text is-small" v-html="table.description"></div>
  </main>
</template>

<script>
import { AgGridVue } from '@ag-grid-community/vue';
import { AllCommunityModules } from '@ag-grid-community/all-modules';
import { mapGetters, mapState } from 'vuex';
import TableTrendLine from '@/components/table/TableTrendLine';
import TableNumberCell from '@/components/table/TableNumberCell';

// Filters
import TableIdFilter from '@/components/table/tableIdFilter.js';

import { agRu } from '@/plugins/ag-ru';

export default {
  components: {
    AgGridVue,
    // eslint-disable-next-line
    TableTrendLine,
    // eslint-disable-next-line
    TableNumberCell,
    FavouriteToggler: () => import(/* webpackChunkName: "FavouriteToggler" */ '@/components/shared/FavouriteToggler'),
    TableSeriesLevel: () => import(/* webpackChunkName: "TableSeriesLevel" */ '@/components/table/TableSeriesLevel')
  },

  metaInfo() {
    return {
      title: this.table.name
    };
  },

  props: {
    trendLine: {
      type: Boolean,
      default: true
    }
  },

  beforeMount() {
    this.gridOptions = {};

    this.frameworkComponents = {
      tableIdFilter: TableIdFilter
    };

    const prepareColumnsForAgGrid = () => {
      const remapColumn = column => {
        if (!column.datafield) {
          return;
        } else if (column.datafield === 'name') {
          return {
            // checkboxSelection: true,
            // headerCheckboxSelection: true,
            headerName: column.text,
            headerTooltip: column.text,
            field: column.datafield,
            tooltipField: column.datafield,
            suppressMenu: true,
            minWidth: 200,
            flex: 2,
            // Filters
            filter: 'agTextColumnFilter',
            filterParams: {
              applyButton: true,
              resetButton: true
            }
          };
        } else {
          return {
            cellRendererFramework: 'TableNumberCell',
            headerName: column.text,
            headerTooltip: column.text,
            field: column.datafield,
            tooltipField: column.datafield,
            suppressMenu: true,
            type: 'numericColumn',
            hide: false,
            flex: 1,
            // Filters
            filter: 'agNumberColumnFilter',
            filterParams: {
              applyButton: true,
              resetButton: true
            }
          };
        }
      };

      function buildIdFilter() {
        function buildFilter() {
          return {
            headerName: 'id',
            field: 'id',
            suppressMenu: true,
            hide: true,
            resizable: false,
            sortable: false,
            filter: 'tableIdFilter',
            valueGetter: params => {
              return params.node.data.id;
            }
          };
        }

        return buildFilter();
      }

      function buildTrendHeader() {
        function buildHeader() {
          return {
            headerName: 'Тренд',
            cellRendererFramework: 'TableTrendLine',
            field: 'trend',
            suppressSizeToFit: true,
            resizable: false,
            sortable: false,
            width: 170,
            minWidth: 170,
            filter: false,
            valueGetter: params => {
              const trendData = [];
              const sourceData = params.node.data;

              for (const key in sourceData) {
                if (key.includes('data')) {
                  trendData.push(sourceData[key]);
                }
              }
              return trendData;
            }
          };
        }

        return buildHeader();
      }

      const trendHeader = this.trendLine ? buildTrendHeader() : {};
      const idFilter = buildIdFilter();

      return [...this.tableData.headers.map(remapColumn), trendHeader, idFilter];
    };

    const prepareRowsForAgGrid = () => {
      const convertValue = value => {
        let outputValue;

        if (value == null) {
          return '-';
        } else if (isNaN(value)) {
          outputValue = Number(value.replace(',', '.'));
          if (isNaN(outputValue)) {
            return '-';
          }
          return +outputValue;
        } else {
          outputValue = value;
          return +outputValue;
        }
      };

      const remapRow = (row, index) => {
        const remappedRow = {};

        for (const [index, field] of this.tableData.fields.entries()) {
          if (field.name.includes('data')) {
            remappedRow[field.name] = convertValue(row.data[index]);
          } else {
            remappedRow[field.name] = row.data[index];
          }
        }

        return {
          ...remappedRow,
          id: this.tableData.rows[index].seriesId,
          level: this.tableData.rows[index].level
        };
      };

      return this.tableData.rows.map(remapRow);
    };

    this.columnDefs = prepareColumnsForAgGrid();
    this.rowData = prepareRowsForAgGrid();
    this.levels = this.buildLevels();

    // Set selection options
    this.rowSelection = 'multiple';

    this.gridOptions.defaultColDef = {
      resizable: true,
      sortable: true,
      minWidth: 50,
      flex: 1
    };

    this.modules = AllCommunityModules;
    this.domLayout = 'autoHeight';
  },

  mounted() {
    this.gridApi = this.gridOptions.api;
    this.gridColumnApi = this.gridOptions.columnApi;
    this.localeText = agRu;
  },

  data() {
    return {
      columnDefs: null,
      rowData: null,
      gridApi: null,
      columnApi: null,
      defaultColDef: null,
      gridOptions: null,
      domLayout: null,
      modules: null,
      localeText: null,
      rowSelection: null,
      frameworkComponents: null,
      //
      series: [],
      toggleSeries: false,
      selectedSeries: [],
      levels: []
    };
  },

  watch: {
    selectedSeries: {
      deep: true,
      handler(newValue, oldValue) {
        if (newValue === oldValue) {
          return;
        }
        // Run filters
        const filterSeries = this.gridOptions.api.getFilterInstance('id');

        filterSeries.setModel({
          value: newValue
        });

        this.gridApi.onFilterChanged();
      }
    }
  },

  methods: {
    onGridReady() {
      // this.autoSizeAll();
    },

    buildLevels() {
      const buildLevels = (accumulator, currentValue) => {
        if (accumulator.some(el => el.level === currentValue.level)) {
          return accumulator;
        } else {
          const level = this.tableData.series.level.find(level => level.id === currentValue.level);
          const thisLevel = {
            level: currentValue.level,
            name: level.name,
            state: true
          };

          return [...accumulator, thisLevel];
        }
      };

      return this.tableData.rows.reduce(buildLevels, []);
    },

    toggleLevel(status, level) {
      const rowsToAction = this.tableData.rows.filter(el => el.level === level).map(row => row.seriesId);

      if (status) {
        this.selectedSeries = this.selectedSeries.filter(item => !rowsToAction.includes(item));
      } else {
        this.selectedSeries = [...new Set([...this.selectedSeries, ...rowsToAction])];
      }
    },

    selectSeries(data) {
      const { status, id } = data;

      if (status) {
        if (this.selectedSeries.find(el => el === id)) {
          const selected = this.selectedSeries.filter(el => el !== id);
          this.selectedSeries = selected;
        }
      } else {
        if (!this.selectedSeries.find(el => el === id)) {
          const selected = [...this.selectedSeries, id];
          this.selectedSeries = selected;
        }
      }
    },

    clearFilters() {
      this.selectedSeries = [];
      this.gridOptions.api.setFilterModel(null);
      this.gridApi.onFilterChanged();
    },

    toggleColumnVisibility(e, column) {
      this.gridColumnApi.setColumnVisible(column.field, e);

      if (this.trendLine) {
        const trend = this.gridColumnApi.getColumn('trend');
      }
    },

    autoSizeAll() {
      var allColumnIds = [];

      this.gridColumnApi.getAllColumns().forEach(column => {
        if (column.colDef.resizable === true) {
          allColumnIds.push(column.colId);
        }
      });

      this.gridColumnApi.autoSizeColumns(allColumnIds);
    },

    exportCSV() {
      const selectedRows = this.gridApi.getSelectedRows();
      const columns = this.gridColumnApi.getAllColumns();
      const columnKeysToExport = columns.reduce(getColumnForExport, []);

      function getColumnForExport(accumulator, currentValue) {
        if (currentValue.visible && currentValue.colId !== 'trend') {
          return [...accumulator, currentValue.colId];
        }

        return accumulator;
      }

      function processCellCallback(cell) {
        if (cell.column.colDef.type === 'numericColumn') {
          return cell.value.toString().replace('.', ',');
        }

        return cell.value;
      }

      const params = {
        // suppressQuotes: true,
        customHeader: `${this.table.name}\n${this.table.measure}\n`,
        onlySelected: selectedRows.length > 0 ? true : false,
        columnSeparator: ';',
        columnGroups: true,
        columnKeys: columnKeysToExport,
        fileName: this.table.name,
        processCellCallback: processCellCallback
      };

      this.gridApi.exportDataAsCsv(params);
    },

    isLevelSelected(level) {
      const levelRows = this.tableData.rows.filter(el => el.level === level);

      if (this.selectedSeries.every(id => !levelRows.some(level => level.seriesId === id))) {
        return true;
      } else return false;
    }
  },

  computed: {
    ...mapState({
      tableData: state => state.table.tableData,
      table: state => state.table.table
    }),

    ...mapGetters({ isAuth: 'isAuth' }),

    dataColumns() {
      return this.columnDefs.filter(el => el.field.includes('data'));
    },

    isFiltered() {
      if (this.selectedSeries.length > 0) {
        return false;
      } else return true;
    },

    topLevelRows() {
      return this.tableData.rows.filter(row => !row.parent || row.parent === '');
    }
  }
};
</script>

<style scoped lang="scss">
.full-height {
  height: auto;
}

.fixed-height {
  height: 70vh;
  min-height: 40rem;
}
</style>
